import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMotivationalQuote, getMoodFoods, getUserEnrollments, getEntitlements, getPrograms } from '../lib/api';
import { IMAGES, SKETCH_PATTERNS, programImage } from '../lib/images';
import { Link } from 'react-router-dom';
import useDailyStreak from '../hooks/useDailyStreak';
import {
  Sun, Moon, Cloud, Smile, Dumbbell, BookOpen, ChevronRight, Loader2,
  Brain, Sparkles, Puzzle, Flame, Crown, Heart, Palette, ArrowRight,
} from 'lucide-react';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Good Morning', icon: Sun };
  if (h < 17) return { text: 'Good Afternoon', icon: Cloud };
  return { text: 'Good Evening', icon: Moon };
}

const moodColors = {
  happy: '#FFD93D', stressed: '#6BCB77', tired: '#4D96FF', anxious: '#9B59B6',
  'low-energy': '#E74C3C', focused: '#3498DB', bloated: '#1ABC9C',
  'craving-sweet': '#FF6B9D', angry: '#E67E22', grateful: '#E91E63',
};

export default function HomePage() {
  const { user, deviceId } = useAuth();
  const [quote, setQuote] = useState(null);
  const [moodFoods, setMoodFoods] = useState([]);
  const [activePrograms, setActivePrograms] = useState([]);
  const [defaultProgram, setDefaultProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const streak = useDailyStreak();

  const greeting = getGreeting();
  const GreetIcon = greeting.icon;

  useEffect(() => {
    async function load() {
      try {
        const [quoteRes, moodRes, enrollRes, entRes, progRes] = await Promise.allSettled([
          getMotivationalQuote(),
          getMoodFoods(),
          getUserEnrollments(deviceId, user?.id || user?._id || user?.user_id),
          getEntitlements(deviceId, user?.email),
          getPrograms(),
        ]);
        if (quoteRes.status === 'fulfilled') setQuote(quoteRes.value.data);
        if (moodRes.status === 'fulfilled') setMoodFoods(moodRes.value.data?.slice(0, 6) || []);

        // Pick the 7-day detox as the default starter program; fall back to first
        if (progRes.status === 'fulfilled') {
          const all = progRes.value.data || [];
          const detox = all.find(p => p.id === '7-day-detox' || p.id === 'detox_7d') || all[0];
          if (detox) setDefaultProgram(detox);
        }

        // Active programs (enrolled + entitled)
        const programMap = progRes.status === 'fulfilled'
          ? Object.fromEntries((progRes.value.data || []).map(p => [p.id, p]))
          : {};
        const enrollArr = enrollRes.status === 'fulfilled'
          ? (Array.isArray(enrollRes.value.data) ? enrollRes.value.data : (enrollRes.value.data?.enrollments || []))
          : [];
        const entitledProgramIds = entRes.status === 'fulfilled' ? (entRes.value.data?.programs || []) : [];
        const byId = {};
        enrollArr.forEach(e => {
          const isActive = !e.status || e.status === 'active' || e.status === 'in_progress';
          if (!isActive) return;
          const pid = e.program_id || e.programId;
          if (!pid) return;
          byId[pid] = {
            program_id: pid,
            program_name: e.program_name || programMap[pid]?.name || pid,
            current_day: e.current_day || 1,
            duration: programMap[pid]?.duration,
          };
        });
        entitledProgramIds.forEach(pid => {
          if (!byId[pid]) byId[pid] = {
            program_id: pid,
            program_name: programMap[pid]?.name || pid,
            current_day: 1,
            duration: programMap[pid]?.duration,
          };
        });
        setActivePrograms(Object.values(byId));
      } catch {}
      setLoading(false);
    }
    load();
  }, [deviceId, user]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-10 sm:space-y-14" data-testid="home-dashboard">

      {/* ─── Header: greeting + streak + quote ─────────────────────── */}
      <section className="space-y-5 animate-fade-in">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <GreetIcon className="w-6 h-6" style={{ color: 'var(--accent)' }} />
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
              {greeting.text}, {user?.name?.split(' ')[0] || 'there'}
            </h1>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                borderColor: '#FFB74D60', color: '#E65100', fontFamily: 'Manrope',
              }}
              title={streak > 1 ? `You've shown up ${streak} days in a row — keep it going!` : 'Welcome! Visit daily to build your streak.'}
              data-testid="daily-streak-badge">
              <Flame className="w-3.5 h-3.5" style={{ color: '#FB8C00' }} />
              {streak}-day streak
            </span>
          </div>
          <p className="text-sm ml-9" style={{ color: 'var(--muted-fg)' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {quote && (
          <div className="rounded-2xl p-6 sm:p-8 relative overflow-hidden shadow-md"
            style={{ background: 'linear-gradient(135deg, #5A7264 0%, #879B7D 60%, #A5B598 100%)', color: '#FEFBF3' }}
            data-testid="quote-card">
            <div className="absolute top-0 right-0 w-56 h-56 opacity-15"
              style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'right top' }} />
            <div className="flex items-center gap-2 mb-3 relative z-10">
              <Sparkles className="w-4 h-4" />
              <p className="text-xs uppercase tracking-widest opacity-85 font-semibold" style={{ fontFamily: 'Manrope' }}>Daily Inspiration</p>
            </div>
            <p className="text-lg sm:text-xl font-medium leading-relaxed mb-3 relative z-10" style={{ fontFamily: 'Manrope' }}>"{quote.quote}"</p>
            <p className="text-sm opacity-85 relative z-10">— {quote.author}</p>
          </div>
        )}
      </section>

      {/* ─── Section 1: KNOW YOURSELF (IQ + EQ) ────────────────────── */}
      <SectionHeader
        kicker="Know Yourself"
        title="Discover what makes you, you."
        subtitle="Two short tests. One moment of clarity." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* IQ */}
        <Link to="/personality/iq" data-testid="hero-iq-card"
          className="group rounded-2xl p-7 sm:p-8 border relative overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl"
          style={{ background: 'linear-gradient(135deg, #EEF4FF 0%, #DBE8FF 60%, #C9DAFF 100%)', borderColor: '#A8C0F5' }}>
          <div className="absolute top-0 right-0 w-44 h-44 opacity-15"
            style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }} />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: 'white' }}>
                <Puzzle className="w-5 h-5" />
              </div>
              <span className="text-xs uppercase tracking-widest font-bold" style={{ color: '#4338CA', fontFamily: 'Manrope' }}>Cognitive Test</span>
            </div>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: '#1E1B4B' }}>
              Your IQ Score
            </h3>
            <p className="text-sm leading-relaxed mb-5" style={{ color: '#3730A3' }}>
              20 timed questions across 4 cognitive domains. Get your number, percentile, and a category breakdown.
            </p>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#4338CA' }}>
              Take the IQ Test <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </Link>
        {/* EQ */}
        <Link to="/personality" data-testid="hero-eq-card"
          className="group rounded-2xl p-7 sm:p-8 border relative overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl"
          style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #E8D5FE 60%, #DCC1FE 100%)', borderColor: '#C9A9F2' }}>
          <div className="absolute top-0 right-0 w-44 h-44 opacity-15"
            style={{ backgroundImage: `url("${SKETCH_PATTERNS.leafBranch}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }} />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)', color: 'white' }}>
                <Brain className="w-5 h-5" />
              </div>
              <span className="text-xs uppercase tracking-widest font-bold" style={{ color: '#6B21A8', fontFamily: 'Manrope' }}>Personality Test</span>
            </div>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: '#3B0764' }}>
              Your Wellness Archetype
            </h3>
            <p className="text-sm leading-relaxed mb-5" style={{ color: '#6B21A8' }}>
              Find your emotional patterns, decision-making style, and the wellness routine that fits how you actually work.
            </p>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#6B21A8' }}>
              Take the EQ Test <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </Link>
      </div>

      {/* ─── Section 2: BUILD YOURSELF (Programs) ──────────────────── */}
      <SectionHeader
        kicker="Build Yourself"
        title="Guided wellness programs."
        subtitle="Bite-sized daily steps. Real, lasting habits." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Default starter — 7-day detox with 10% promo */}
        <Link to="/programs?promo=BEGIN10" data-testid="hero-program-card"
          className="lg:col-span-2 group rounded-2xl border overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl relative"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="grid sm:grid-cols-2">
            <div className="h-48 sm:h-auto sm:min-h-[260px] bg-cover bg-center relative"
              style={{
                backgroundImage: `url(${defaultProgram?.id ? programImage(defaultProgram.id) : IMAGES.gratitude})`,
                backgroundColor: '#F0E8D8',
              }}>
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold"
                style={{ background: '#16A34A', color: 'white', boxShadow: '0 4px 12px rgba(22,163,74,0.4)' }}
                data-testid="hero-program-discount">
                10% OFF · BEGIN10
              </span>
            </div>
            <div className="p-6 sm:p-7 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}>
                  <Dumbbell className="w-4 h-4" />
                </div>
                <span className="text-xs uppercase tracking-widest font-bold" style={{ color: '#047857', fontFamily: 'Manrope' }}>Featured · Starter</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 leading-tight" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                {defaultProgram?.name || '7-Day Detox Starter'}
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-fg)' }}>
                A gentle one-week reset with curated meals, movement, and mindset prompts. Perfect first step.
              </p>
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#047857' }}>
                Get started with 10% off <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        </Link>

        {/* Active / All programs */}
        <div className="rounded-2xl p-6 border animate-fade-in"
          style={{ background: 'linear-gradient(135deg, #F9E8E0 0%, #F2D5C4 100%)', borderColor: '#E0B199' }}
          data-testid="active-programs-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ fontFamily: 'Manrope', color: '#8B4A2F' }}>Your programs</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#D97941', color: 'white' }}>
              <Dumbbell className="w-5 h-5" />
            </div>
          </div>
          {activePrograms.length > 0 ? (
            <div className="space-y-2.5">
              {activePrograms.slice(0, 3).map((e) => (
                <Link key={e.program_id} to={`/programs`}
                  className="flex items-center gap-2.5 p-2 rounded-lg transition-all hover:bg-white/60"
                  data-testid={`active-program-${e.program_id}`}>
                  <div className="w-2 h-2 rounded-full" style={{ background: '#D97941' }} />
                  <span className="text-sm flex-1 font-medium truncate" style={{ color: '#3E1E0F' }}>{e.program_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'rgba(217,121,65,0.18)', color: '#8B4A2F' }}>
                    Day {e.current_day}{e.duration ? `/${e.duration}` : ''}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: '#8B4A2F' }}>You haven't started any program yet.</p>
          )}
          <Link to="/programs" className="flex items-center gap-1 mt-4 text-sm font-semibold" style={{ color: '#C7641F' }}>
            {activePrograms.length > 0 ? 'View all programs' : 'Browse all programs'} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* ─── PREMIUM PROMOTIONAL BANNER (25% off) ──────────────────── */}
      <Link to="/premium?promo=RESET25" data-testid="premium-promo-banner"
        className="group block rounded-2xl overflow-hidden relative transition-all hover:-translate-y-0.5 hover:shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, #1F2937 0%, #2C4A52 50%, #5A7264 100%)',
          color: '#FEFBF3',
          boxShadow: '0 12px 36px rgba(31,41,55,0.25)',
        }}>
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("${SKETCH_PATTERNS.waves}")`,
            backgroundSize: '600px',
            backgroundRepeat: 'repeat-x',
            backgroundPosition: 'center',
          }} />
        <div className="absolute top-0 right-0 w-72 h-72 opacity-10"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'right top' }} />

        <div className="relative z-10 p-6 sm:p-10 md:flex md:items-center md:gap-8">
          <div className="flex-1 mb-5 md:mb-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-4"
              style={{ background: 'rgba(255,215,100,0.18)', color: '#FFD966', border: '1px solid rgba(255,215,100,0.35)' }}>
              <Crown className="w-3.5 h-3.5" />
              LIMITED-TIME · 25% OFF · RESET25
            </div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-3" style={{ fontFamily: 'Manrope' }}>
              Unlock lifetime access for your transformation.
            </h3>
            <p className="text-base leading-relaxed mb-5 max-w-xl" style={{ opacity: 0.85 }}>
              Every program. Every test report. Every guided routine. Pay once, keep forever.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" style={{ opacity: 0.9 }}>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> All Programs</span>
              <span className="inline-flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> All Reports</span>
              <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" /> Lifetime Access</span>
            </div>
          </div>
          <div className="md:flex-shrink-0">
            <div className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-base transition-all group-hover:scale-105"
              style={{ background: '#FFD966', color: '#1F2937', boxShadow: '0 8px 24px rgba(255,217,102,0.35)' }}>
              Claim 25% off
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>

      {/* ─── Quick Check-In ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-3 flex-wrap mb-1.5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--accent)', fontFamily: 'Manrope' }}>
            Quick Check-In
          </p>
          <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{
              background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
              color: '#15803D',
              border: '1px solid #86EFAC',
            }}
            data-testid="free-badge"
          >
            <Sparkles className="w-3 h-3" />
            Free to use
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
          Small moments. Big difference.
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>
          Take 2 minutes to recenter — no signup, no payment, just space to breathe.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Mood */}
        <Link to="/mood" data-testid="quick-mood-card"
          className="group rounded-2xl p-5 border transition-all hover:-translate-y-1 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #FFF1E8 0%, #FFE0CC 100%)', borderColor: '#F4C7A4' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#FF9F68', color: 'white' }}>
            <Smile className="w-5 h-5" />
          </div>
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#8B5A3C' }}>Mood</p>
          <h4 className="text-base font-bold mb-2" style={{ color: '#3E1E0F', fontFamily: 'Manrope' }}>How are you feeling?</h4>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {moodFoods.slice(0, 3).map(m => (
              <span key={m.id} className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{ background: 'rgba(255,255,255,0.7)', color: moodColors[m.id] || '#8B5A3C', border: `1px solid ${moodColors[m.id] || '#F4C7A4'}40` }}>
                {m.mood}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#C7641F' }}>
            Log mood <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Gratitude */}
        <Link to="/gratitude" data-testid="quick-gratitude-card"
          className="group rounded-2xl p-5 border transition-all hover:-translate-y-1 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #FCE7F3 0%, #F9D6E8 100%)', borderColor: '#F0B6CD' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#E91E63', color: 'white' }}>
            <BookOpen className="w-5 h-5" />
          </div>
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#9D174D' }}>Gratitude</p>
          <h4 className="text-base font-bold mb-2" style={{ color: '#500724', fontFamily: 'Manrope' }}>What are you grateful for?</h4>
          <p className="text-xs mb-3" style={{ color: '#9D174D' }}>3 quick lines. End every day lighter.</p>
          <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#9D174D' }}>
            Open journal <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Doodle Express */}
        <Link to="/doodle" data-testid="quick-doodle-card"
          className="group rounded-2xl p-5 border transition-all hover:-translate-y-1 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', borderColor: '#FBBF24' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#F59E0B', color: 'white' }}>
            <Palette className="w-5 h-5" />
          </div>
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#92400E' }}>Doodle Express</p>
          <h4 className="text-base font-bold mb-2" style={{ color: '#451A03', fontFamily: 'Manrope' }}>Draw your day away.</h4>
          <p className="text-xs mb-3" style={{ color: '#92400E' }}>Trace a Zen template or freestyle.</p>
          <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#92400E' }}>
            Start sketching <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Puzzles Reset */}
        <Link to="/puzzles" data-testid="quick-puzzles-card"
          className="group rounded-2xl p-5 border transition-all hover:-translate-y-1 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBE8FE 100%)', borderColor: '#BEDBFE' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#3B82F6', color: 'white' }}>
            <Puzzle className="w-5 h-5" />
          </div>
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#1E40AF' }}>Puzzles Reset</p>
          <h4 className="text-base font-bold mb-2" style={{ color: '#1E3A8A', fontFamily: 'Manrope' }}>Brain break, on demand.</h4>
          <p className="text-xs mb-3" style={{ color: '#1E40AF' }}>Memory Flip · Zen Pattern · Story Whisper</p>
          <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#1E40AF' }}>
            Play now <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      </div>

    </div>
  );
}

// ── Section header (small reusable block) ────────────────────────────
function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div className="animate-fade-in">
      <p className="text-xs uppercase tracking-[0.2em] font-bold mb-1.5" style={{ color: 'var(--accent)', fontFamily: 'Manrope' }}>
        {kicker}
      </p>
      <h2 className="text-2xl sm:text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>{subtitle}</p>
      )}
    </div>
  );
}
